import React from 'react';
import ReactEcharts from 'echarts-for-react';
import {
    Alert,
    AlertRecordValueType,
    AlertRecordWithDate,
} from 'lib/alert/models';
import { differenceInCalendarDays, format } from 'date-fns';
import { formatDisplay } from 'helpers/formatHelper';
import { useTranslation } from 'react-i18next';
import { getVisualMap } from './helpers/helpers';
import type { EChartsOption, DatasetComponentOption } from 'echarts';
import { isEChartTooltipFormatterParams } from 'helpers/eChartHelpers';

const dateFormat = 'dd-MM-yyyy';

type FixedThresholdEvolutionNoDimensionChartData = {
    value: number;
    date: Date | number | string;
};

/**
 * Return the total number of calendar days spanning the range of all records.
 * @param {AlertRecordWithDate[]} records - The records to compute the day span.
 * @returns {number} the number of calendar days.
 */
const numberOfDays = (records: AlertRecordWithDate[]): number => {
    if (records.length === 1) return 1;
    return (
        1 +
        differenceInCalendarDays(
            records[0].date,
            records[records.length - 1].date,
        )
    );
};

type Props = {
    records: AlertRecordWithDate[];
    alert: Alert;
};

const FixedThresholdEvolutionNoDimension = ({ records, alert }: Props) => {
    const { t } = useTranslation();
    if (records.length === 0) {
        return <div>{t('alert.status.noRecords')}</div>;
    }

    const nbDays = numberOfDays(records);

    const lastThreshold = records[0].values.find(
        (v) => v.type === AlertRecordValueType.FIXED_THRESHOLD,
    )?.value;
    const isGreater = alert.condition.operation === 'greater';

    const firstValue = records[0].values.find(
        (v) => v.type === AlertRecordValueType.LONE_ALERT_DATA,
    );
    if (firstValue === undefined) {
        return <div>{t('alert.status.noRecords')}</div>;
    }
    const valueType = firstValue.valueType || 'float';

    const dataset: DatasetComponentOption[] = [
        {
            id: 'base',
            dimensions: [
                { name: 'date', type: 'time' },
                { name: 'value', type: 'float' },
            ],
            source: records.map((r) => ({
                date: r.date.getTime(),
                value: r.values.find(
                    (v) => v.type === AlertRecordValueType.LONE_ALERT_DATA,
                )?.value,
            })),
        },
    ];

    const option: EChartsOption = {
        tooltip: {
            formatter: (data) => {
                if (
                    isEChartTooltipFormatterParams<FixedThresholdEvolutionNoDimensionChartData>(
                        data,
                    )
                ) {
                    const value = formatDisplay(
                        data.data.value,
                        valueType,
                        2,
                        'default',
                    );
                    if (data.componentType === 'series') {
                        const date = format(
                            new Date(data.data.date),
                            dateFormat,
                        );
                        return `<div>${date}<br/>${value}</div>`;
                    }
                    return `<div>${value}</div>`;
                } else {
                    console.error('Invalid tooltip formatter params');
                    return '';
                }
            },
        },
        legend: {},
        dataset,
        xAxis: {
            type: 'time',
            splitNumber: nbDays,
            axisLabel: {
                formatter: (value) => format(value, dateFormat),
                hideOverlap: true,
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value) =>
                    formatDisplay(value, valueType, 2, 'default'),
            },
        },
        visualMap: getVisualMap(lastThreshold, isGreater),
        grid: { containLabel: true, left: 10, right: 10, top: 10, bottom: 10 },
        series: [
            {
                type: 'line',
                smooth: true,
                encode: {
                    x: 'date',
                    y: 'value',
                },
                datasetId: 'base',
                markLine: {
                    symbol: 'none',
                    precision: 100,
                    lineStyle: {
                        type: 'solid',
                        width: 1,
                        color: 'black',
                    },
                    label: {
                        formatter: (data) =>
                            formatDisplay(data.value, valueType, 2, 'default'),
                        ...(isGreater && {
                            position: 'insideEndTop',
                        }),
                        ...(!isGreater && {
                            position: 'insideEndBottom',
                        }),
                    },
                    data: [
                        {
                            name: 'Threshold value',
                            yAxis: lastThreshold,
                        },
                    ],
                },
            },
        ],
    };

    return (
        <ReactEcharts
            notMerge
            option={option}
            className="line-chart"
            style={{
                height: '24vh',
                minHeight: '150px',
            }}
            opts={{ renderer: 'canvas' }}
        />
    );
};

export default FixedThresholdEvolutionNoDimension;
